/* eslint-disable @typescript-eslint/no-explicit-any */
import IndeterminateCheckbox from 'components/base/IndeterminateCheckbox';
import SelectionCountBadge from 'components/common/SelectionCountBadge';
import { PropsWithChildren, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  InitialTableState,
  VisibilityState,
  PaginationState
} from '@tanstack/react-table';

interface UseAdvanceTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  selection?: boolean;
  sortable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  rowCount?: number;
  manualPagination?: boolean;
  selectionColumnWidth?: number | string;
  initialState?: InitialTableState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  onPaginationChange?: (pagination: PaginationState) => void;
}

const selectionColumn = {
  id: 'select',
  accessorKey: '',
  header: ({ table }: any) => (
    <div className="d-flex align-items-center">
      <IndeterminateCheckbox
        className="form-check fs-8 mb-0"
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler()
        }}
      />
      <SelectionCountBadge count={table.getSelectedRowModel().rows.length} />
    </div>
  ),
  cell: ({ row }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler()
      }}
    />
  ),
  meta: {
    headerProps: { style: { width: '60px' } }
  }
};

const useAdvanceTable = <T,>({
  columns,
  data,
  selection,
  sortable,
  pagination,
  pageSize,
  initialState,
  rowCount,
  manualPagination,
  onColumnVisibilityChange,
  onPaginationChange
}: PropsWithChildren<UseAdvanceTableProps<T>>) => {
  
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [manualPaginationState, setManualPagination] = useState<PaginationState>({
    pageSize: pageSize ?? 10,
    pageIndex: 0
  });
  const [columnFilters, setColumnFilters] = useState(
    initialState?.columnFilters || []
  );

  const table = useReactTable<T>({
    data,
    rowCount,
    
    columns: selection ? [selectionColumn, ...columns] : columns,
    enableSorting: sortable,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    state: {
      columnVisibility,
      columnFilters,
      pagination: manualPagination ? manualPaginationState : undefined,
    },
    
    onPaginationChange: (updater) => {
      setManualPagination((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      );
      if (onPaginationChange) {
        onPaginationChange(
          typeof updater === "function" ? updater(manualPaginationState) : updater
        );
      }
      
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      );
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(
          typeof updater === "function" ? updater(columnVisibility) : updater
        );
      }
    },
    onColumnFiltersChange: setColumnFilters,
  });

  return table;
};

export default useAdvanceTable;
